// =============================================================================
// C-Bridge — CSV Import wizard
// =============================================================================
//
// 6-staps UI voor klant-facing bulk import van dossiers/leads/contactpersonen.
// Route: /onboarding/import  én  /admin/import
// =============================================================================

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Upload,
  FileText,
  Download,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader2,
  ShieldCheck,
  Database,
} from 'lucide-react';
import {
  uploadCsv,
  validateCsvRows,
  executeCsvImport,
  getImportJobStatus,
  templateDownloadUrl,
  type EntityType,
  type FieldDef,
  type HeaderInfoResponse,
  type ValidationResponse,
  type RowValidationError,
  type ExecuteSyncResponse,
  type ExecuteAsyncResponse,
  type JobStatusResponse,
} from '../hooks/useCsvImport.js';

type WizardStep = 1 | 2 | 3 | 4 | 5 | 6;

const ENTITY_OPTIONS: Array<{ value: EntityType; label: string; description: string }> = [
  { value: 'dossier', label: 'Dossiers', description: 'Klantendossiers (SMART, Excel, etc.)' },
  { value: 'lead', label: 'Leads', description: 'Prospecten en CRM-contacten' },
  { value: 'contactpersoon', label: 'Contactpersonen', description: 'Aanspreekpunten bij B2B-klanten' },
];

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Upload bestand',
  2: 'Kolommen mappen',
  3: 'Preview data',
  4: 'Validatie',
  5: 'Import uitvoeren',
  6: 'Resultaat',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function recordsToCsv(
  records: Array<Record<string, string | number | null | undefined>>,
  headers: string[],
  delimiter: ',' | ';' = ';',
): string {
  const needsQuote = new RegExp(`["${delimiter}\\r\\n]`);
  const encode = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return needsQuote.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(encode).join(delimiter)];
  for (const rec of records) lines.push(headers.map((h) => encode(rec[h])).join(delimiter));
  return lines.join('\r\n');
}

function downloadText(filename: string, text: string, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob(['\uFEFF' + text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CSVImportPage() {
  const [step, setStep] = useState<WizardStep>(1);
  const [entityType, setEntityType] = useState<EntityType>('dossier');
  const [file, setFile] = useState<File | null>(null);
  const [headerInfo, setHeaderInfo] = useState<HeaderInfoResponse | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [allRows, setAllRows] = useState<Array<Record<string, string>>>([]);
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [importResult, setImportResult] = useState<ExecuteSyncResponse | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Reset helpers ──────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    setStep(1);
    setFile(null);
    setHeaderInfo(null);
    setMappings({});
    setAllRows([]);
    setValidation(null);
    setImportResult(null);
    setJobStatus(null);
    setError(null);
  }, []);

  // ── Step 1: Upload ─────────────────────────────────────────────────────────

  const handleFileSelect = useCallback(
    async (selected: File) => {
      setError(null);
      if (!selected.name.toLowerCase().endsWith('.csv')) {
        setError('Alleen .csv bestanden zijn toegestaan (Excel: "Opslaan als CSV UTF-8").');
        return;
      }
      if (selected.size > 10 * 1024 * 1024) {
        setError('Bestand is te groot (max 10 MB).');
        return;
      }
      setFile(selected);
      setLoading(true);
      try {
        const info = await uploadCsv(selected, entityType);
        setHeaderInfo(info);
        setMappings(info.suggestedMappings);

        // Server geeft max sampleRows terug — voor de volledige import hebben we alle rijen.
        // Parse client-side opnieuw voor de volledige dataset (snel voor <10k rijen).
        const text = await selected.text();
        const parsed = clientSideParse(text, info.delimiter);
        setAllRows(parsed);

        setStep(2);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [entityType],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const f = e.dataTransfer.files[0];
      if (f) void handleFileSelect(f);
    },
    [handleFileSelect],
  );

  // ── Step 2→3: confirm mapping ──────────────────────────────────────────────

  const missingRequired = useMemo(() => {
    if (!headerInfo) return [];
    const mapped = new Set(Object.values(mappings).filter(Boolean));
    return headerInfo.fieldsCatalog.filter((f) => f.required && !mapped.has(f.key));
  }, [mappings, headerInfo]);

  const goToPreview = () => {
    if (missingRequired.length > 0) {
      setError(`Verplichte velden ontbreken: ${missingRequired.map((f) => f.label).join(', ')}`);
      return;
    }
    setError(null);
    setStep(3);
  };

  // ── Step 3→4: validate ────────────────────────────────────────────────────

  const runValidation = async () => {
    if (!headerInfo) return;
    setLoading(true);
    setError(null);
    try {
      const res = await validateCsvRows(entityType, mappings, allRows);
      setValidation(res);
      setStep(4);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 4→5: execute ─────────────────────────────────────────────────────

  const runImport = async () => {
    setLoading(true);
    setError(null);
    setStep(5);
    try {
      const res = await executeCsvImport(entityType, mappings, allRows);
      if (res.async) {
        // Poll job
        const async = res as ExecuteAsyncResponse;
        await pollJob(async.jobId);
      } else {
        setImportResult(res as ExecuteSyncResponse);
        setStep(6);
      }
    } catch (err) {
      setError((err as Error).message);
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  const pollJob = async (jobId: string): Promise<void> => {
    let attempts = 0;
    const maxAttempts = 600; // 600 × 1s = 10 min
    return new Promise((resolve, reject) => {
      const tick = async () => {
        attempts++;
        try {
          const status = await getImportJobStatus(jobId);
          setJobStatus(status);
          if (status.status === 'done') {
            setImportResult({
              async: false,
              created: status.result?.created ?? 0,
              skipped: status.result?.skipped ?? 0,
              errors: status.result?.errors ?? [],
            });
            setStep(6);
            resolve();
            return;
          }
          if (status.status === 'error') {
            setError(status.errorMessage ?? 'Import mislukt in de achtergrond');
            setStep(4);
            reject(new Error(status.errorMessage));
            return;
          }
          if (attempts >= maxAttempts) {
            setError('Import duurt te lang, probeer opnieuw');
            reject(new Error('timeout'));
            return;
          }
          setTimeout(tick, 1000);
        } catch (err) {
          reject(err);
        }
      };
      void tick();
    });
  };

  // ── Download error report ─────────────────────────────────────────────────

  const downloadErrorReport = () => {
    if (!validation && !importResult) return;
    const errs: RowValidationError[] = [
      ...(validation?.errors ?? []),
      ...(importResult?.errors ?? []),
    ];
    if (errs.length === 0) return;
    const records = errs.map((e) => ({
      rowIndex: e.rowIndex,
      field: e.field,
      message: e.message,
      rawValue: e.rawValue ?? '',
    }));
    const csv = recordsToCsv(records, ['rowIndex', 'field', 'message', 'rawValue'], ';');
    downloadText(`csv-import-errors-${Date.now()}.csv`, csv);
  };

  // ─── Render helpers ───────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">CSV Import</h1>
          <p className="text-gray-600 mt-1">
            Importeer bestaande dossiers, leads of contactpersonen uit SMART, Excel of een ander systeem.
          </p>
        </header>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8 bg-white p-4 rounded-lg shadow-sm">
          {([1, 2, 3, 4, 5, 6] as WizardStep[]).map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold ${
                  step >= s ? 'bg-brand text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > s ? <CheckCircle size={16} /> : s}
              </div>
              <div className="ml-2 text-xs text-gray-600 hidden md:block">{STEP_LABELS[s]}</div>
              {i < 5 && <div className={`flex-1 h-0.5 mx-2 ${step > s ? 'bg-brand' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {/* ── Step 1: upload ──────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Kies type en upload bestand</h2>

            <label className="block text-sm font-medium text-gray-700 mb-2">Wat wilt u importeren?</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              {ENTITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setEntityType(opt.value)}
                  className={`p-4 rounded-lg border-2 text-left transition ${
                    entityType === opt.value
                      ? 'border-brand bg-brand/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{opt.label}</div>
                  <div className="text-xs text-gray-600 mt-1">{opt.description}</div>
                </button>
              ))}
            </div>

            <div className="mb-4 flex items-center justify-between">
              <a
                href={templateDownloadUrl(entityType)}
                className="inline-flex items-center gap-2 text-sm text-brand hover:underline"
              >
                <Download size={14} /> Download {entityType}-sjabloon
              </a>
              <span className="text-xs text-gray-500">Max 10 MB, max 10.000 rijen, UTF-8 CSV</span>
            </div>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition ${
                isDragging ? 'border-brand bg-brand/5' : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <Upload className="mx-auto text-gray-400" size={32} />
              <p className="mt-2 text-sm text-gray-700">
                Sleep een CSV-bestand hierheen of <span className="text-brand font-semibold">klik om te selecteren</span>
              </p>
              {file && <p className="mt-2 text-xs text-gray-500">{file.name} ({Math.round(file.size / 1024)} KB)</p>}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFileSelect(f);
              }}
            />

            {loading && (
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="animate-spin" size={16} /> Bezig met analyseren...
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: mapping ─────────────────────────────────────────────── */}
        {step === 2 && headerInfo && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Koppel kolommen aan Kordaro velden</h2>
            <p className="text-sm text-gray-600 mb-4">
              We hebben {headerInfo.headers.length} kolommen gevonden in uw bestand ({headerInfo.totalRows} rijen). Controleer de
              automatische mapping en pas aan waar nodig.
            </p>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Kolom in uw bestand</th>
                    <th className="text-left px-4 py-2 font-medium">Kordaro veld</th>
                    <th className="text-left px-4 py-2 font-medium">Voorbeeld waarde</th>
                  </tr>
                </thead>
                <tbody>
                  {headerInfo.headers.map((h) => (
                    <tr key={h} className="border-t">
                      <td className="px-4 py-2 font-medium text-gray-900">{h}</td>
                      <td className="px-4 py-2">
                        <select
                          value={mappings[h] ?? ''}
                          onChange={(e) =>
                            setMappings((prev) => ({ ...prev, [h]: e.target.value }))
                          }
                          className="border rounded px-2 py-1 w-full text-sm"
                        >
                          <option value="">— Niet importeren —</option>
                          {headerInfo.fieldsCatalog.map((f: FieldDef) => (
                            <option key={f.key} value={f.key}>
                              {f.label}
                              {f.required ? ' *' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs max-w-xs truncate">
                        {headerInfo.sampleRows[0]?.[h] ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {missingRequired.length > 0 && (
              <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Nog te mappen: {missingRequired.map((f) => f.label).join(', ')}
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <button onClick={reset} className="text-sm text-gray-600 hover:underline flex items-center gap-1">
                <ArrowLeft size={14} /> Annuleren
              </button>
              <button
                onClick={goToPreview}
                disabled={missingRequired.length > 0}
                className="bg-brand text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Doorgaan <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: preview ─────────────────────────────────────────────── */}
        {step === 3 && headerInfo && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview (eerste 10 rijen)</h2>
            <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 sticky top-0">
                  <tr>
                    {Object.entries(mappings)
                      .filter(([, v]) => v)
                      .map(([h, v]) => (
                        <th key={h} className="text-left px-3 py-2 font-medium">
                          {headerInfo.fieldsCatalog.find((f) => f.key === v)?.label ?? v}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {headerInfo.sampleRows.slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-t">
                      {Object.entries(mappings)
                        .filter(([, v]) => v)
                        .map(([h]) => (
                          <td key={h} className="px-3 py-2 text-gray-800 whitespace-nowrap">
                            {row[h] ?? ''}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-between">
              <button onClick={() => setStep(2)} className="text-sm text-gray-600 hover:underline flex items-center gap-1">
                <ArrowLeft size={14} /> Terug
              </button>
              <button
                onClick={runValidation}
                disabled={loading}
                className="bg-brand text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />}
                Valideer {headerInfo.totalRows} rijen
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: validation ──────────────────────────────────────────── */}
        {step === 4 && validation && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Validatie-resultaat</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-xs text-green-700 uppercase font-semibold">Geldige rijen</div>
                <div className="text-2xl font-bold text-green-800 mt-1">{validation.validRowCount}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-xs text-red-700 uppercase font-semibold">Foutieve rijen</div>
                <div className="text-2xl font-bold text-red-800 mt-1">{validation.errorRowCount}</div>
              </div>
            </div>

            {validation.errors.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Eerste 20 fouten</h3>
                  <button
                    onClick={downloadErrorReport}
                    className="text-xs text-brand hover:underline flex items-center gap-1"
                  >
                    <Download size={12} /> Download error-rapport
                  </button>
                </div>
                <div className="border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Rij</th>
                        <th className="text-left px-3 py-2">Veld</th>
                        <th className="text-left px-3 py-2">Fout</th>
                        <th className="text-left px-3 py-2">Waarde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.errors.slice(0, 20).map((e, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-1.5">{e.rowIndex >= 0 ? e.rowIndex + 2 : '—'}</td>
                          <td className="px-3 py-1.5 font-mono">{e.field}</td>
                          <td className="px-3 py-1.5 text-red-700">{e.message}</td>
                          <td className="px-3 py-1.5 text-gray-500">{e.rawValue ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="mt-6 flex justify-between">
              <button onClick={() => setStep(3)} className="text-sm text-gray-600 hover:underline flex items-center gap-1">
                <ArrowLeft size={14} /> Terug
              </button>
              <button
                onClick={runImport}
                disabled={validation.validRowCount === 0}
                className="bg-brand text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                <Database size={14} /> Importeer {validation.validRowCount} rijen
              </button>
            </div>
          </div>
        )}

        {/* ── Step 5: executing (progress) ────────────────────────────────── */}
        {step === 5 && (
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <Loader2 className="mx-auto animate-spin text-brand" size={32} />
            <h2 className="mt-3 text-lg font-semibold">Bezig met importeren...</h2>
            {jobStatus && (
              <div className="mt-4 max-w-md mx-auto">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-brand h-2 rounded-full transition-all"
                    style={{ width: `${(jobStatus.processed / Math.max(jobStatus.total, 1)) * 100}%` }}
                  />
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  {jobStatus.processed} / {jobStatus.total} rijen verwerkt
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 6: result ──────────────────────────────────────────────── */}
        {step === 6 && importResult && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="text-green-600" size={28} />
              <h2 className="text-lg font-semibold text-gray-900">Import voltooid</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-xs text-green-700 uppercase font-semibold">Succesvol aangemaakt</div>
                <div className="text-2xl font-bold text-green-800 mt-1">{importResult.created}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="text-xs text-amber-700 uppercase font-semibold">Overgeslagen / fout</div>
                <div className="text-2xl font-bold text-amber-800 mt-1">{importResult.skipped}</div>
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <button
                onClick={downloadErrorReport}
                className="text-xs text-brand hover:underline flex items-center gap-1 mb-4"
              >
                <Download size={12} /> Download error-rapport ({importResult.errors.length} regels)
              </button>
            )}

            <div className="flex gap-3">
              <button
                onClick={reset}
                className="bg-brand text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2"
              >
                <FileText size={14} /> Nieuwe import starten
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CSVImportPage;

// ─── Client-side CSV parser (dupliceert server-parser voor snelle preview) ──

function clientSideParse(input: string, delimiter: ',' | ';'): Array<Record<string, string>> {
  if (input.charCodeAt(0) === 0xfeff) input = input.slice(1);
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuote = false;
  for (let i = 0; i < input.length; i++) {
    const c = input[i];
    if (inQuote) {
      if (c === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuote = false;
        }
      } else field += c;
      continue;
    }
    if (c === '"') {
      inQuote = true;
      continue;
    }
    if (c === delimiter) {
      row.push(field);
      field = '';
      continue;
    }
    if (c === '\r') {
      if (input[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
      continue;
    }
    if (c === '\n') {
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
      continue;
    }
    field += c;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  const nonEmpty = rows.filter((r) => r.some((cell) => cell.trim() !== ''));
  if (nonEmpty.length === 0) return [];
  const [headerRow, ...dataRows] = nonEmpty;
  const headers = headerRow.map((h) => h.trim());
  return dataRows.map((r) => {
    const rec: Record<string, string> = {};
    headers.forEach((h, idx) => {
      rec[h] = (r[idx] ?? '').trim();
    });
    return rec;
  });
}
